import { useState, useMemo } from 'react';
import { Collapse } from '@mui/material';
import { Chip, Button } from '@coordio/ui';
import { ChevronDown as ExpandMoreIcon, ChevronUp as ExpandLessIcon, Bot as AgentIcon, CheckCircle as CheckIcon, CircleX as ErrorIcon } from 'lucide-react';
import { ToolCallCard } from './ToolCallCard';
import type { OrchestratorEvent } from './useOrchestratorStream';

interface SubAgentCardProps {
  callId: string;
  events: OrchestratorEvent[];
  allEvents?: Map<string, OrchestratorEvent[]>;
}

export const SubAgentCard = ({ callId, events }: SubAgentCardProps) => {
  const [expanded, setExpanded] = useState(true);

  const startEvent = events.find((e) => e.type === 'sub_agent_started');
  const completedEvent = events.find((e) => e.type === 'sub_agent_completed');
  const alias = (startEvent?.data.alias as string) ?? callId;
  const task = (startEvent?.data.task as string) ?? '';
  const depth = (startEvent?.data.depth as number) ?? 1;
  const isComplete = !!completedEvent;
  const isSuccess = completedEvent?.data.success as boolean | undefined;

  const streamedText = useMemo(() => {
    return events
      .filter((e) => e.type === 'sub_agent_token')
      .map((e) => e.data.token as string)
      .join('');
  }, [events]);

  const toolCalls = events.filter((e) => e.type === 'sub_agent_tool_call');

  const borderClass = !isComplete
    ? 'border-blue-400'
    : isSuccess
      ? 'border-green-500'
      : 'border-red-500';

  const statusIcon = !isComplete
    ? <AgentIcon size={16} className="text-blue-500" />
    : isSuccess
      ? <CheckIcon size={16} className="text-green-600" />
      : <ErrorIcon size={16} className="text-red-600" />;

  return (
    <div
      className={`my-1 rounded border bg-card ${borderClass} ${depth > 1 ? 'ml-4' : ''}`}
    >
      <div
        className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-muted"
        onClick={() => setExpanded((v) => !v)}
      >
        {statusIcon}
        <Chip label={alias} color="accent" variant="outlined" />
        <span className="ml-1 flex-1 truncate text-xs text-muted-foreground">
          {task.length > 80 ? task.slice(0, 80) + '...' : task}
        </span>
        <Button variant="ghost" size="icon" className="h-6 w-6">
          {expanded ? <ExpandLessIcon size={16} /> : <ExpandMoreIcon size={16} />}
        </Button>
      </div>
      {!isComplete && (
        <div className="h-0.5 w-full animate-pulse bg-blue-400" />
      )}
      <Collapse in={expanded}>
        <div className="px-3 pb-3">
          {task && (
            <p className="mb-2 text-sm italic text-muted-foreground">
              {task}
            </p>
          )}

          {toolCalls.map((tc) => (
            <ToolCallCard
              key={tc.id}
              toolName={tc.data.toolName as string}
              args={tc.data.arguments as string}
              isRunning={!isComplete}
            />
          ))}

          {streamedText && (
            <div
              className="mt-2 whitespace-pre-wrap break-words rounded bg-muted p-2 text-[13px]"
            >
              {streamedText}
            </div>
          )}

          {completedEvent && !isSuccess && Boolean(completedEvent.data.errorMessage) && (
            <p className="mt-2 text-sm text-red-600">
              {completedEvent.data.errorMessage as string}
            </p>
          )}

          {completedEvent && isSuccess && Boolean(completedEvent.data.result) && (
            <div
              className="mt-2 max-h-[200px] overflow-auto whitespace-pre-wrap break-words rounded bg-green-600 p-2 text-[13px] text-white"
            >
              {(completedEvent.data.result as string).slice(0, 500)}
              {(completedEvent.data.result as string).length > 500 ? '...' : ''}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};
